import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CheckCircle, ArrowRight, Briefcase, UserCircle, ShieldCheck, Star } from "@phosphor-icons/react";
import LangSwitcher from "@/components/LangSwitcher";
import { useI18n } from "@/lib/i18n";
import { useEffect, useState } from "react";
import { API } from "@/lib/api";

function ReviewsFeed() {
  const [items, setItems] = useState([]);
  useEffect(() => { fetch(`${API}/public/reviews-feed`).then(r => r.ok ? r.json() : []).then(setItems).catch(() => {}); }, []);
  if (!items.length) return null;
  return (
    <div>
      <h2 className="font-display text-3xl sm:text-4xl font-extrabold mb-8 text-center">Recent 5★ shifts</h2>
      <div className="grid md:grid-cols-3 gap-4">
        {items.slice(0, 6).map((r) => (
          <div key={r.id} className="card-tactile p-5" data-testid={`review-feed-${r.id}`}>
            <div className="flex gap-0.5 mb-2">{Array.from({ length: 5 }).map((_, i) => <Star key={`star-${i}`} size={14} weight="fill" className="text-yellow-500" />)}</div>
            <div className="text-slate-800 text-sm leading-relaxed">"{r.comment || 'Great shift!'}"</div>
            <div className="mt-3 text-xs text-slate-500">— {r.reviewer_name} · {r.reviewer_role} · {r.job_title}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

const HERO_IMG = "https://images.unsplash.com/photo-1653762379954-8943c787e78b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxzbWlsaW5nJTIwcGVyc29uJTIwdXNpbmclMjBwaG9uZXxlbnwwfHx8fDE3ODY0OTg1Mjh8MA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const { t } = useI18n();
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="font-display text-2xl font-extrabold text-slate-900" data-testid="brand-logo">
            Shift<span className="text-blue-600">Flex</span>
          </Link>
          <div className="flex items-center gap-2">
            <LangSwitcher compact />
            <Link to="/login" data-testid="header-login-link" className="text-sm font-semibold text-slate-700 hover:text-blue-600 px-4 py-2">{t("btn.log_in")}</Link>
            <Link to="/register" data-testid="header-register-link">
              <Button className="bg-slate-900 hover:bg-slate-800 text-white rounded-md h-10 px-5 font-semibold">{t("btn.get_started")}</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-70" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="label-caps mb-4">UK Supply Staff Marketplace</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight">
              {t("hero.title_1")}<br />{t("hero.title_2")}<br />
              <span className="text-blue-600">{t("hero.title_3")}</span>
            </h1>
            <p className="mt-6 text-lg text-slate-700 leading-relaxed max-w-xl">
              {t("hero.sub")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register?role=staff" data-testid="cta-find-work">
                <Button className="bg-blue-600 hover:bg-blue-700 text-white rounded-md h-12 px-7 font-semibold text-base">
                  {t("cta.find_work")} <ArrowRight size={18} className="ml-1" />
                </Button>
              </Link>
              <Link to="/register?role=company" data-testid="cta-find-staff">
                <Button variant="outline" className="border-slate-300 rounded-md h-12 px-7 font-semibold text-base">
                  {t("cta.find_staff")}
                </Button>
              </Link>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
              {[["10k+", "Shifts booked"],["1,200+", "Vetted staff"],["4.8★", "Average rating"]].map(([n,l]) => (
                <div key={l}>
                  <div className="font-display text-2xl font-extrabold text-slate-900">{n}</div>
                  <div className="label-caps mt-1">{l}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 bg-blue-600/10 rounded-md rotate-1" aria-hidden />
            <img src={HERO_IMG} alt="Worker using ShiftFlex" className="relative rounded-md w-full h-[480px] object-cover border border-slate-200" />
            <div className="absolute -bottom-6 -left-6 bg-white rounded-md border border-slate-200 shadow-lg p-4 max-w-[240px]">
              <div className="flex items-center gap-2 mb-1"><CheckCircle size={18} weight="fill" className="text-emerald-500" /><span className="font-semibold text-sm">Shift confirmed</span></div>
              <div className="text-xs text-slate-600">Warehouse Operative · ABC Logistics · £12.50/hr</div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid md:grid-cols-2 gap-12">
          {[{
            title: "For Staff", icon: UserCircle,
            steps: ["Register in minutes", "Complete your profile", "Set availability", "Browse suitable shifts", "Book with one tap"]
          },{
            title: "For Companies", icon: Briefcase,
            steps: ["Register your business", "Post a shift", "Review matched staff", "Confirm bookings", "Manage your workforce"]
          }].map((col) => (
            <div key={col.title}>
              <div className="flex items-center gap-3 mb-6">
                <col.icon size={32} weight="regular" className="text-blue-600" />
                <h2 className="font-display text-3xl font-extrabold text-slate-900">{col.title}</h2>
              </div>
              <ol className="space-y-3">
                {col.steps.map((s, i) => (
                  <li key={s} className="flex items-start gap-3 bg-white border border-slate-200 rounded-md p-4">
                    <span className="w-7 h-7 shrink-0 rounded-full bg-blue-600 text-white text-sm font-bold flex items-center justify-center">{i+1}</span>
                    <span className="font-medium text-slate-800">{s}</span>
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </div>
      </section>

      {/* Benefits */}
      <section className="bg-slate-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold mb-12">Why ShiftFlex</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: ShieldCheck, title: "Vetted talent", body: "Every worker is document-verified before their first shift." },
              { icon: Star, title: "Rate as you go", body: "Two-way reviews build a trusted community over time." },
              { icon: CheckCircle, title: "No double-booking", body: "Server-side clash detection keeps schedules clean." },
            ].map((f) => (
              <div key={f.title} className="p-6 bg-slate-800 border border-slate-700 rounded-md">
                <f.icon size={28} className="text-yellow-400 mb-3" />
                <div className="font-display text-xl font-bold mb-2">{f.title}</div>
                <div className="text-slate-300 text-sm leading-relaxed">{f.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <ReviewsFeed />
      </section>

      <footer className="border-t border-slate-200 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-slate-600">
          <div>© 2026 ShiftFlex. All rights reserved.</div>
          <div className="flex gap-4"><span>Privacy</span><span>Terms</span><span>Cookies</span></div>
        </div>
      </footer>
    </div>
  );
}
